export const MINUS = "−";

const LOCALE = "en-US";

const moneyFormats = new Map<string, Intl.NumberFormat>();

const moneyFormat = (kind: "short" | "signed" | "compact", currency: string): Intl.NumberFormat => {
  const key = `${kind}:${currency}`;
  const cached = moneyFormats.get(key);
  if (cached) return cached;
  const format = new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency,
    notation: "compact",
    ...(kind === "compact"
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumSignificantDigits: 3, signDisplay: kind === "signed" ? "always" : "auto" }),
  });
  moneyFormats.set(key, format);
  return format;
};

const countCompact = new Intl.NumberFormat(LOCALE, { notation: "compact", maximumFractionDigits: 2 });

const percentOneDecimal = new Intl.NumberFormat(LOCALE, {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const signedPercent = new Intl.NumberFormat(LOCALE, {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "always",
});

const pointsOneDecimal = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const withMinus = (formatted: string) => formatted.replace("-", MINUS);

export const formatMoneyShort = (value: number, currency: string): string =>
  moneyFormat("short", currency).format(Math.abs(value));

export const formatSignedMoneyShort = (value: number, currency: string): string =>
  withMinus(moneyFormat("signed", currency).format(value));

export const formatMoneyCompact = (value: number, currency: string): string =>
  withMinus(moneyFormat("compact", currency).format(value));

export const formatComparisonTip = (converted: number, currency: string): string =>
  `${formatMoneyCompact(converted, currency)} in ${currency}, for comparing across regions`;

export const formatCountCompact = (value: number): string => withMinus(countCompact.format(value));

export const formatPercentValue = (value: number, digits = 2): string =>
  new Intl.NumberFormat(LOCALE, {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value / 100);

export const formatChangeMagnitude = (change: number, unit: "percent" | "points"): string =>
  unit === "points"
    ? `${pointsOneDecimal.format(Math.abs(change))}pt`
    : percentOneDecimal.format(Math.abs(change) / 100);

export const formatSignedPercent = (change: number): string => withMinus(signedPercent.format(change / 100));

export const percentChange = (current: number | null, prior: number | null): number | null => {
  if (current === null || prior === null || prior === 0) return null;
  return (current / prior - 1) * 100;
};
